import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import { getPartnerAuthHeaders } from "@/lib/apiAuth";
import { toast } from "sonner";
import { AlertTriangle, Loader2, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const extractErrorMessage = (err, fallback) => {
  if (err?.response?.data?.detail) {
    const detail = err.response.data.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  }
  if (err?.response?.data?.message) return err.response.data.message;
  if (err?.request && !err?.response) {
    return "Network error. Please check your connection and try again.";
  }
  return fallback;
};

const formatAccessUntil = (isoDate) => {
  if (!isoDate) return "the end of your current billing period";
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return isoDate;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const StopBillingModal = ({ client, open, onClose, onSuccess }) => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [periodEnd, setPeriodEnd] = useState(client?.billingStopAt || null);
  const [periodLoading, setPeriodLoading] = useState(false);

  useEffect(() => {
    if (!open || !client) return;

    setSubmitError("");
    setPeriodEnd(client.billingStopAt || null);

    if (!getPartnerAuthHeaders().Authorization || client.billingStopAt) return;

    setPeriodLoading(true);
    axios
      .get(`${BACKEND_URL}/api/partner/billing/summary`, {
        headers: getPartnerAuthHeaders(),
      })
      .then((res) => {
        setPeriodEnd(res.data?.current_period_end || null);
      })
      .catch(() => setPeriodEnd(null))
      .finally(() => setPeriodLoading(false));
  }, [open, client]);

  if (!open || !client) return null;

  const accessUntilLabel = formatAccessUntil(periodEnd);

  const getAuthHeader = () => getPartnerAuthHeaders();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    setSubmitError("");

    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/partner/clients/${client.id}/stop-billing`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success("Billing stop scheduled");
      onSuccess?.(res.data);
      onClose();
    } catch (err) {
      if (err?.response?.status === 401) {
        Cookies.remove("token");
        Cookies.remove("email");
        navigate("/login");
        return;
      }
      const message = extractErrorMessage(err, "Could not schedule billing stop. Please try again.");
      setSubmitError(message);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
      data-testid="stop-billing-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-amber-500/30 bg-[#11151F] shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 sm:p-6 border-b border-partner">
          <div>
            <h2 className="text-lg font-bold text-partner">Stop billing</h2>
            <p className="text-sm text-partner-muted font-body mt-1">{client.name}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-partner-muted hover:text-partner hover:bg-white/5"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-5">
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
            <div className="text-sm font-body text-amber-100/90 space-y-2">
              <p>
                Stop billing for this client?{" "}
                <strong className="text-amber-100">
                  Wholesale billing for this slot stops immediately.
                </strong>{" "}
                You won&apos;t be charged for this client on your next invoice. The AI Employee{" "}
                <strong className="text-amber-100">keeps running until {periodLoading ? "…" : accessUntilLabel}</strong>.
                After that, access stops.
              </p>
              <p className="text-amber-200/80">
                <strong>Client data and settings are not deleted.</strong>
              </p>
            </div>
          </div>

          {submitError && (
            <p className="text-sm text-red-400 font-body" data-testid="stop-billing-error">
              {submitError}
            </p>
          )}

          <div className="flex flex-col-reverse sm:flex-row gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2.5 rounded-lg border border-partner text-partner-muted font-body text-sm hover:text-partner hover:bg-[#080A0F] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || periodLoading}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-amber-500/20 text-amber-200 border border-amber-500/40 font-semibold text-sm hover:bg-amber-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="stop-billing-submit"
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {isSubmitting ? "Scheduling…" : "Stop billing"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StopBillingModal;
